import React, { ReactNode, useEffect, useState, useCallback, useRef, useContext } from "react";
import { SettingsContext } from "utils/context";
import { clearSelection } from "utils/utils";

import "./Splitter.scss";

type Props = {
    orientation?: "horizontal" | "vertical";
    primaryFraction: number;
    primaryMinSize?: number;
    primary: ReactNode;
    secondaryMinSize?: number;
    secondary: ReactNode;
};

export default function Splitter({
    orientation,
    primaryFraction,
    primaryMinSize,
    primary,
    secondaryMinSize,
    secondary
}: Props) {
    const [dragging, setDragging] = useState(false);
    const [fraction, setFraction] = useState(primaryFraction);
    const [secondarySize, setSecondarySize] = useState<number>();
    const [{ verticalOrientation: vertical }] = useContext(SettingsContext);
    //const vertical = useMemo(() => orientation === "vertical", [orientation]);
    const containerRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);

    useEffect(() => clearSelection(), [dragging]);

    const evalFraction = useCallback(() => {
        const containerRect = containerRef.current?.getBoundingClientRect();
        const thumbRect = thumbRef.current?.getBoundingClientRect();
        const containerSize = vertical ? containerRect?.height! : containerRect?.width!;
        const thumbSize = vertical ? thumbRect?.height! : thumbRect?.width!;
        const totalSize = containerSize - thumbSize;
        setSecondarySize((totalSize * (100 - fraction)) / 100);
    }, [fraction, vertical]);

    useEffect(() => {
        evalFraction();
    }, [evalFraction]);

    const getSizes = useCallback(
        (x: number, y: number) => {
            const containerRect = containerRef.current?.getBoundingClientRect();
            const thumbRect = thumbRef.current?.getBoundingClientRect();
            const containerSize = vertical ? containerRect?.height! : containerRect?.width!;
            const thumbSize = vertical ? thumbRect?.height! : thumbRect?.width!;
            const totalSize = containerSize - thumbSize;
            const containerEdge = vertical ? containerRect?.top! : containerRect?.left!;

            const pMin = Math.min(totalSize / 2, primaryMinSize ?? 100);
            const sMin = Math.min(totalSize / 2, secondaryMinSize ?? 100);
            let pSize = (vertical ? y - containerEdge : x - containerEdge) - thumbSize / 2;
            if (pSize < pMin) {
                pSize = pMin;
            } else if (pSize > totalSize - sMin) {
                pSize = totalSize - sMin;
            }
            const sSize = Math.max(sMin, totalSize - pSize);
            return [pSize, sSize];
        },
        [vertical, primaryMinSize, secondaryMinSize]
    );

    const onMouseMove = useCallback(
        (event: MouseEvent) => {
            if (dragging) {
                const [p, s] = getSizes(event.clientX, event.clientY);
                setFraction((100 * p) / (p + s));
            }
        },
        [dragging, getSizes]
    );

    useEffect(() => {
        document.addEventListener("mousemove", onMouseMove);
        return () => {
            document.removeEventListener("mousemove", onMouseMove);
        };
    }, [onMouseMove]);

    useEffect(() => {
        const onResize = () => evalFraction();
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [evalFraction]);

    useEffect(() => {
        if (dragging) {
            document.body.classList.add("splitter-dragging");
        } else {
            document.body.classList.remove("splitter-dragging");
        }
    }, [dragging]);

    useEffect(() => {
        const onMouseUp = () => setDragging(false);
        document.addEventListener("mouseup", onMouseUp);
        return () => {
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className={`splitter${vertical ? " splitter-vertical" : " splitter-horizontal"}${
                dragging ? " splitter-dragging" : ""
            }`}
        >
            <div className="splitter-panel splitter-panel-primary">{primary}</div>
            <div ref={thumbRef} className="splitter-thumb" onMouseDown={() => setDragging(true)}>
                <div className={`splitter-bars`} />
            </div>
            <div className="splitter-panel" style={{ [vertical ? "height" : "width"]: secondarySize }}>
                {secondary}
            </div>
        </div>
    );
}
